<p-dialog
  header="Import Answers"
  [(visible)]="visible"
  (onHide)="close()"
  [modal]="true"
  [style]="{ width: '900px' }"
  [closable]="!isImporting"
>
  <p-toast></p-toast>

  <!-- ── STEP 1: Upload ── -->
  <ng-container *ngIf="step === 'upload'">
    <div class="import-header p-mb-3">
      <label class="file-label">
        <i class="pi pi-upload p-mr-1"></i> Choose CSV file
        <input type="file" accept=".csv" (change)="onFileChange($event)" />
      </label>
      <button pButton type="button" label="Download Template" icon="pi pi-download"
        class="p-button-text p-button-sm p-ml-2" (click)="downloadTemplate()">
      </button>
    </div>

    <div class="hint p-mb-3">
      <div class="hint-section">
        <strong>Answer fields</strong>
        <ul class="hint-list">
          <li><code>title</code> <span class="required">required</span> — questions match their answers by this title</li>
          <li><code>image_ref</code> — asset name</li>
          <li><code>animation_triggers</code> — pipe-separated <em>Trigger:Option</em> pairs (e.g. <em>Head Animation:Nod|Body Animation:Idle</em>)</li>
          <li><code>enabled</code> — <em>true</em> or <em>false</em> (defaults to true)</li>
          <li><code>env</code> — comma-separated (e.g. <em>dev,qa,prod</em>)</li>
        </ul>
      </div>
      <div class="hint-section">
        <span class="hint-note">
          Import Answers first, then import Questions — the Questions import looks up existing answers by title.
        </span>
      </div>
    </div>

    <p *ngIf="parsedRows.length" class="parsed-count">
      <i class="pi pi-check-circle p-mr-1" style="color: #22c55e"></i>
      {{ parsedRows.length }} rows loaded — click <strong>Preview</strong> to resolve refs before importing.
    </p>
  </ng-container>

  <!-- ── STEP 2: Preview ── -->
  <ng-container *ngIf="step === 'preview'">
    <div class="preview-summary p-mb-3">
      <p-tag severity="success" [value]="validRowCount + ' ready'" class="p-mr-1"></p-tag>
      <p-tag *ngIf="warnRowCount" severity="warning" [value]="warnRowCount + ' with warnings'" class="p-mr-1"></p-tag>
      <p-tag *ngIf="invalidRowCount" severity="danger" [value]="invalidRowCount + ' invalid (will skip)'"></p-tag>
    </div>

    <div *ngIf="pendingImportConfirm" class="warn-confirm p-mb-3">
      <i class="pi pi-exclamation-triangle"></i>
      <span>Row{{ warnRowNums.length > 1 ? 's' : '' }} <strong>{{ warnRowNums.join(', ') }}</strong> have warnings. Are you sure you want to proceed?</span>
    </div>

    <p-table [value]="resolvedRows" [paginator]="resolvedRows.length > 10" [rows]="10" styleClass="p-datatable-sm preview-table">
      <ng-template pTemplate="header">
        <tr>
          <th style="width:40px">#</th>
          <th>Title</th>
          <th>Image</th>
          <th>Animation Triggers</th>
          <th>Enabled</th>
          <th>Env</th>
          <th>Warnings</th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-r>
        <tr [class.row-invalid]="!r.payload" [class.row-warning]="r.payload && r.warnings.length">
          <td>{{ r.rowNum }}</td>
          <td class="truncate">{{ r.title }}</td>
          <td>{{ r.payload?.image_ref ? '✓' : (r.payload ? '—' : '') }}</td>
          <td class="trigger-cell">{{ getTriggerSummary(r.triggers) || '—' }}</td>
          <td>{{ r.payload ? (r.payload.enabled ? 'true' : 'false') : '' }}</td>
          <td>{{ r.payload?.env?.join(',') || '—' }}</td>
          <td class="warnings-cell">
            <span *ngIf="!r.payload" class="warn-text error-text">Missing title</span>
            <span *ngFor="let w of r.warnings" class="warn-text">{{ w }}</span>
          </td>
        </tr>
      </ng-template>
    </p-table>
  </ng-container>

  <!-- ── STEP 3: Results ── -->
  <ng-container *ngIf="step === 'results'">
    <div class="result-summary p-mb-3">
      <p-tag severity="success" [value]="successCount + ' created'" class="p-mr-1"></p-tag>
      <p-tag *ngIf="skippedCount" severity="warning" [value]="skippedCount + ' skipped'" class="p-mr-1"></p-tag>
      <p-tag *ngIf="errorCount" severity="danger" [value]="errorCount + ' failed'"></p-tag>
    </div>
    <p-table [value]="results" [paginator]="results.length > 15" [rows]="15" styleClass="p-datatable-sm">
      <ng-template pTemplate="header">
        <tr>
          <th style="width:50px">Row</th>
          <th>Title</th>
          <th style="width:90px">Status</th>
          <th>Message</th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-r>
        <tr [class.row-success]="r.status === 'success'" [class.row-warning]="r.status === 'skipped'" [class.row-invalid]="r.status === 'error'">
          <td>{{ r.row }}</td>
          <td class="truncate">{{ r.title }}</td>
          <td>
            <p-tag [severity]="r.status === 'success' ? 'success' : r.status === 'skipped' ? 'warning' : 'danger'"
              [value]="r.status">
            </p-tag>
          </td>
          <td class="result-message">{{ r.message || '' }}</td>
        </tr>
      </ng-template>
    </p-table>
  </ng-container>

  <ng-template pTemplate="footer">
    <ng-container *ngIf="step === 'upload'">
      <button pButton type="button" label="Cancel" class="p-button-text" (click)="close()"></button>
      <button pButton type="button" label="Preview" icon="pi pi-eye"
        [disabled]="!parsedRows.length || isLoading" [loading]="isLoading" (click)="preview()">
      </button>
    </ng-container>
    <ng-container *ngIf="step === 'preview'">
      <button pButton type="button" label="Back" icon="pi pi-arrow-left" class="p-button-text" (click)="back()"></button>
      <button *ngIf="pendingImportConfirm" pButton type="button" label="Cancel" class="p-button-text p-button-secondary" (click)="cancelImportConfirm()"></button>
      <button pButton type="button"
        [label]="isImporting ? progress : pendingImportConfirm ? 'Yes, proceed' : 'Import ' + validRowCount + ' answers'"
        [icon]="pendingImportConfirm ? 'pi pi-exclamation-triangle' : 'pi pi-check'"
        [class.p-button-warning]="pendingImportConfirm"
        [disabled]="!validRowCount || isImporting"
        [loading]="isImporting"
        (click)="import()">
      </button>
    </ng-container>
    <ng-container *ngIf="step === 'results'">
      <button pButton type="button" label="Close" (click)="close()"></button>
    </ng-container>
  </ng-template>
</p-dialog>
